import {
  CloudWatchLogsClient,
  CreateLogStreamCommand,
  CreateLogStreamCommandInput,
  InputLogEvent,
  PutLogEventsCommand,
  PutLogEventsCommandInput,
  PutLogEventsCommandOutput,
} from '@aws-sdk/client-cloudwatch-logs';
import { LogEntry, deleteOldRecords, getLoggerDb } from './store';

import config from '@/config';
import { createSectionLogger } from '.';
import { fetchAuthSession } from 'aws-amplify/auth';
import { formatLogDate } from '@/utils/dateManipulation';
import { getModelName, getPlatform } from '../plugins/device';
import { getUsername, isSignedIn } from '../auth';
import { postLogEntries } from '@/api/loggerProxy/service';

/**
 * Only ever the debug trace, which is off unless `devLogControl.cloudwatch`
 * says otherwise. This module drains the log queue, so anything it logged
 * about an upload would be queued and uploaded on the next run; its own
 * failures go to the console instead.
 */
export const logger = createSectionLogger('cloudwatch');

const LIMITS = {
  MAX_EVENT_MSG_SIZE_BYTES: 256000, // The real max size is 262144, we leave some room for overhead on each message
  MAX_BATCH_SIZE_BYTES: 1000000, // We leave some fudge factor here too.
};

// CloudWatch adds 26 bytes per log event based on their documentation:
// https://docs.aws.amazon.com/AmazonCloudWatchLogs/latest/APIReference/API_PutLogEvents.html
const BASE_EVENT_SIZE_BYTES = 26;

// PutLogEvents rejects the whole request if any event falls outside this
// window, so entries outside it are dropped rather than sent.
const MAX_EVENT_AGE_MS = 14 * 864e5;
const MAX_EVENT_FUTURE_MS = 2 * 3600 * 1000;

// Used for the first run of a session and while a backlog remains; the
// configured interval covers the steady state.
const SHORT_DELAY_MS = 30 * 1000;

// How many times a batch is retried before it is dropped. Entries are kept
// across a failed upload, so without a ceiling one batch the server always
// rejects would hold up everything queued behind it until it aged out.
const MAX_UPLOAD_ATTEMPTS = 5;

let timerHandle: ReturnType<typeof setTimeout> | undefined;
let running = false;
let failedUploads = 0;

const createLogStream = async (
  client: CloudWatchLogsClient,
  logStreamName: string,
): Promise<boolean> => {
  try {
    const payload: CreateLogStreamCommandInput = {
      logGroupName: config.services.cloudWatch.logGroupName,
      logStreamName,
    };
    await client.send(new CreateLogStreamCommand(payload));
  } catch (error: any) {
    // Another writer getting there first is not a failure; anything else
    // means the batch cannot be written.
    if (error?.name !== 'ResourceAlreadyExistsException') {
      console.error('createLogStream', error?.name, error?.message);
      return false;
    }
  }

  return true;
};

/** `noStream` is worth telling apart: it is the one failure worth retrying. */
type PutResult = 'sent' | 'noStream' | 'failed';

const putLogEvents = async (
  client: CloudWatchLogsClient,
  logEvents: InputLogEvent[],
  logStreamName: string,
): Promise<PutResult> => {
  try {
    const payload: PutLogEventsCommandInput = {
      logGroupName: config.services.cloudWatch.logGroupName,
      logStreamName,
      logEvents,
    };

    const command = new PutLogEventsCommand(payload);
    const result: PutLogEventsCommandOutput = await client.send(command);

    if (result.$metadata.httpStatusCode !== 200) {
      console.error('putLogEvents', result.$metadata);
      return 'failed';
    }

    return 'sent';
  } catch (error: any) {
    if (error?.name === 'ResourceNotFoundException') {
      return 'noStream';
    }

    // SDK errors serialise to an empty object, so pull out the parts that say
    // why the call was rejected.
    console.error('putLogEvents', error?.name, error?.message);
    return 'failed';
  }
};

type Batch = {
  /** Events to upload, oldest first, all falling on one local date. */
  events: InputLogEvent[];
  /** Ids of every entry the batch consumed, including any dropped unsent. */
  ids: number[];
  /** Whether a limit cut the batch short, leaving entries queued behind it. */
  more: boolean;
};

/** The last millisecond of the local day a timestamp falls in. */
const endOfLocalDay = (timestamp: number): number => {
  const date = new Date(timestamp);

  return (
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() +
    864e5 -
    1
  );
};

/**
 * Takes entries from the head of the queue until one of the CloudWatch limits
 * is reached. A batch covers a single local day because the stream it is
 * written to is named after that date.
 *
 * An entry CloudWatch would reject - one too large, or timestamped outside the
 * window it accepts - is consumed without being sent: the whole request fails
 * on a single bad event, so leaving one queued would block everything behind
 * it for as long as it takes to age out.
 */
const collectBatch = (entries: LogEntry[]): Batch => {
  const events: InputLogEvent[] = [];
  const ids: number[] = [];
  const now = Date.now();
  let bytes = 0;
  let batchDate = 0;
  let more = false;

  for (const entry of entries) {
    if (entry.id === undefined) {
      continue;
    }

    if (ids.length >= config.services.cloudWatch.batchSize) {
      more = true;
      break;
    }

    if (
      entry.timestamp < now - MAX_EVENT_AGE_MS ||
      entry.timestamp > now + MAX_EVENT_FUTURE_MS
    ) {
      console.warn('CloudWatch timestamp out of range, dropping', {
        date: new Date(entry.timestamp),
      });
      ids.push(entry.id);
      continue;
    }

    if (!batchDate) {
      batchDate = endOfLocalDay(entry.timestamp);
    } else if (entry.timestamp > batchDate) {
      more = true;
      break;
    }

    // The id is the queue's own key, not part of the log.
    const { id, ...logged } = entry;
    const message = JSON.stringify(logged);
    const evSize =
      new TextEncoder().encode(message).length + BASE_EVENT_SIZE_BYTES;

    if (evSize > LIMITS.MAX_EVENT_MSG_SIZE_BYTES) {
      console.warn('CloudWatch log too large, dropping', { evSize });
      ids.push(id);
      continue;
    }

    if (bytes + evSize > LIMITS.MAX_BATCH_SIZE_BYTES) {
      more = true;
      break;
    }

    bytes += evSize;
    events.push({ timestamp: entry.timestamp, message });
    ids.push(id);
  }

  return { events, ids, more };
};

const uploadBatch = async (events: InputLogEvent[]): Promise<boolean> => {
  const platform = await getPlatform();
  const appVersion = config.app.version as string;
  // The batch's own date, not today's: a batch collected either side of
  // midnight, or after the app has been shut overnight, belongs in the stream
  // for the day it was logged.
  const date = formatLogDate(new Date(events[0].timestamp as number));

  if (await isSignedIn()) {
    return writeToCloudWatch(platform, appVersion, date, events);
  }

  return writeToLogggerProxy(platform, appVersion, date, events);
};

/**
 * Uploads one batch. Entries are removed only once the upload is confirmed, so
 * a failed send or a page destroyed mid-upload retries them on the next run
 * instead of losing them. That makes the queue grow while uploads fail, which
 * `deleteOldRecords` bounds: an entry that can never be sent ages out of the
 * queue rather than blocking everything behind it forever.
 *
 * Resolves true when entries are still queued and the next run should be soon.
 */
const runBatch = async (): Promise<boolean> => {
  const db = getLoggerDb();
  if (!db) {
    return false;
  }

  deleteOldRecords();

  // One more than the batch size, so a full batch can tell whether the queue
  // held exactly that many entries or has more waiting.
  const entries = await db.logEntries
    .orderBy('timestamp')
    .limit(config.services.cloudWatch.batchSize + 1)
    .toArray();

  if (entries.length === 0) {
    return false;
  }

  const { events, ids, more } = collectBatch(entries);

  if (events.length === 0) {
    await db.logEntries.bulkDelete(ids);
    return ids.length > 0;
  }

  logger.debug('CloudWatch uploading batch', {
    eventCount: events.length,
    more,
  });

  if (!(await uploadBatch(events))) {
    failedUploads += 1;

    if (failedUploads < MAX_UPLOAD_ATTEMPTS) {
      console.error('CloudWatch upload failed, entries kept', {
        eventCount: events.length,
        failedUploads,
      });
      return false;
    }

    console.error('CloudWatch upload failed too often, dropping batch', {
      eventCount: events.length,
      failedUploads,
    });
  }

  failedUploads = 0;

  await db.logEntries.bulkDelete(ids);

  return more;
};

const timerFunction = async () => {
  running = true;

  let delayMs = config.services.cloudWatch.intervalSecs * 1000;

  try {
    if (await runBatch()) {
      delayMs = SHORT_DELAY_MS;
    }
  } catch (error: any) {
    console.error('CloudWatchTimer', error?.message ?? error);
  } finally {
    running = false;
    // Always rescheduled: a run that uploads nothing, or throws, must not be
    // the last one of the session.
    timerHandle = setTimeout(timerFunction, delayMs);
  }
};

/**
 * Starts, or re-arms, the upload timer. startServices calls this on ready,
 * resume and signin: each call replaces the pending run rather than adding a
 * timer of its own, which also recovers the chain if it was lost while the app
 * was suspended. A run already in flight reschedules itself, so it is left
 * alone.
 */
export const startCloudWatchTimer = (): void => {
  if (running) {
    return;
  }

  clearTimeout(timerHandle);
  timerHandle = setTimeout(timerFunction, SHORT_DELAY_MS);
};

const writeToCloudWatch = async (
  platform: string,
  appVersion: string,
  date: string,
  batch: InputLogEvent[],
): Promise<boolean> => {
  const { credentials } = await fetchAuthSession();

  if (!credentials) {
    console.error('writeToCloudWatch: no credentials available');
    return false;
  }

  const awsLogClient = new CloudWatchLogsClient({
    region: config.services.cloudWatch.region,
    credentials,
  });

  const cognitoId = await getUsername();
  const logStreamName = `${date}-${platform}-${appVersion}-${cognitoId}`;

  // Written to before the stream is known to exist: the stream for a given
  // date and user is created once and written to for the rest of that day, so
  // asking for it first would mean a redundant call - or a rejected one - on
  // every upload but the first.
  const result = await putLogEvents(awsLogClient, batch, logStreamName);
  if (result !== 'noStream') {
    return result === 'sent';
  }

  if (!(await createLogStream(awsLogClient, logStreamName))) {
    return false;
  }

  return (await putLogEvents(awsLogClient, batch, logStreamName)) === 'sent';
};

const writeToLogggerProxy = async (
  platform: string,
  appVersion: string,
  date: string,
  batch: InputLogEvent[],
): Promise<boolean> => {
  const logGroupName = config.services.cloudWatch.logGroupName;
  const modelName = await getModelName();

  return await postLogEntries(
    logGroupName,
    `${date}-${platform}-${appVersion}-${modelName}`,
    batch,
  );
};

/**
 * Sends a single entry straight to the logger proxy, bypassing the queue that
 * startCloudWatchTimer drains.
 *
 * That queue first uploads 30 seconds after startup and then on the configured
 * interval, so an entry written immediately before the page navigates away is
 * still sitting in IndexedDB when the page is destroyed. Use this for entries
 * that have to survive a redirect. The proxy holds the credentials, so unlike
 * the CloudWatch client this works for signed-out users.
 */
export const logToProxyNow = async (
  message: string,
  data: Record<string, any>,
): Promise<boolean> => {
  try {
    const platform = await getPlatform();
    const modelName = await getModelName();
    const date = formatLogDate(new Date());
    const timestamp = Date.now();

    return await postLogEntries(
      config.services.cloudWatch.logGroupName,
      `${date}-${platform}-${config.app.version}-${modelName}`,
      [
        {
          timestamp,
          message: JSON.stringify({ timestamp, level: 'error', message, data }),
        },
      ],
    );
  } catch (error: any) {
    console.error('logToProxyNow', error?.message ?? error);
    return false;
  }
};
